import { useEffect, useRef, useState } from "react";
import { apiBaseUrl, getMyClient, uploadMyProfilePhoto } from "../lib/api";
import { useAuth } from "../modules/auth/AuthContext";
import type { Client } from "../types";

function ProfileIcon({ name }: { name: "user" | "shield" | "mail" | "camera" | "id" | "badge" | "target" | "calendar" | "height" | "weight" | "ruler" | "phone" | "plan" | "trophy" }) {
  const paths = {
    user: <><circle cx="12" cy="8" r="4" /><path d="M4 21a8 8 0 0 1 16 0" /></>,
    shield: <path d="M12 3 5 6v5c0 5 3 8 7 10 4-2 7-5 7-10V6z" />,
    mail: <><rect x="3" y="5" width="18" height="14" rx="2" /><path d="m3 7 9 6 9-6" /></>,
    camera: <><path d="M7 7h2l1.5-2h3L15 7h2a3 3 0 0 1 3 3v6a3 3 0 0 1-3 3H7a3 3 0 0 1-3-3v-6a3 3 0 0 1 3-3Z" /><circle cx="12" cy="13" r="3" /></>,
    id: <><rect x="3" y="5" width="18" height="14" rx="2" /><circle cx="9" cy="12" r="2.5" /><path d="M14 10h5M14 14h3" /></>,
    badge: <><path d="M12 3 4 7v6c0 4.5 3.5 8 8 8s8-3.5 8-8V7z" /><path d="m9 12 2 2 4-4" /></>,
    target: <><circle cx="12" cy="12" r="8" /><circle cx="12" cy="12" r="3" /><path d="M12 2v4M22 12h-4" /></>,
    calendar: <><rect x="4" y="5" width="16" height="15" rx="2" /><path d="M8 3v4M16 3v4M4 10h16" /></>,
    height: <><path d="M12 3v18" /><path d="m8 7 4-4 4 4M8 17l4 4 4-4" /></>,
    weight: <><rect x="5" y="4" width="14" height="16" rx="3" /><path d="M9 10a3 3 0 0 1 6 0" /></>,
    ruler: <><rect x="2" y="9" width="20" height="6" rx="1" /><path d="M6 9v3M10 9v4M14 9v3M18 9v4" /></>,
    phone: <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.91.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.9.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92z" />,
    plan: <><rect x="4" y="4" width="16" height="16" rx="2" /><path d="M8 10h8M8 14h5" /><circle cx="17" cy="14" r="1.5" /></>,
    trophy: <><path d="M8 21h8M12 17v4M7 4h10v5a5 5 0 0 1-10 0z" /><path d="M7 6H4a2 2 0 0 0 2 4M17 6h3a2 2 0 0 1-2 4" /></>,
  };

  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      {paths[name]}
    </svg>
  );
}

export function ClientProfilePage() {
  const { user, updateProfilePhoto } = useAuth();
  const [client, setClient] = useState<Client | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [photoError, setPhotoError] = useState<string | null>(null);
  const [photoSuccess, setPhotoSuccess] = useState(false);

  useEffect(() => {
    getMyClient().then(setClient).catch(() => undefined);
  }, []);

  const fullName = `${user?.first_name ?? ""} ${user?.last_name ?? ""}`.trim() || "Cliente";
  const initials = fullName
    .split(" ")
    .map((part) => part[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  const profilePhotoSrc = user?.profile_photo_url
    ? `${apiBaseUrl}${user.profile_photo_url}`
    : null;

  async function handlePhotoChange(event: React.ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      setPhotoError("El archivo debe ser una imagen.");
      setPhotoSuccess(false);
      return;
    }
    setPhotoError(null);
    setPhotoSuccess(false);
    setUploadingPhoto(true);
    try {
      const { profile_photo_url } = await uploadMyProfilePhoto(file);
      updateProfilePhoto(profile_photo_url);
      setPhotoSuccess(true);
      setTimeout(() => setPhotoSuccess(false), 2500);
    } catch {
      setPhotoError("No se pudo subir la foto. Intenta nuevamente.");
    } finally {
      setUploadingPhoto(false);
    }
  }

  const hasPhysicalData = Boolean(
    client?.age || client?.heightCm || client?.currentWeightKg || client?.targetWeightKg || client?.sex
  );
  const hasGoalData = Boolean(client?.objective || client?.planName || client?.notes);

  function formatPlanDate(value?: string) {
    if (!value) return "";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleDateString("es-PE", { day: "2-digit", month: "long", year: "numeric" });
  }

  return (
    <section className="profile-page">
      <header className="profile-hero">
        <span className="profile-hero-avatar">
          {profilePhotoSrc ? (
            <img src={profilePhotoSrc} alt={`Foto de perfil de ${fullName}`} />
          ) : (
            initials
          )}
        </span>
        <div className="profile-hero-body">
          <p className="profile-hero-eyebrow">Mi perfil</p>
          <h1>{fullName}</h1>
          <p className="profile-hero-sub">{user?.email || "Sin correo registrado"}</p>
          <div className="profile-hero-actions">
            <button
              type="button"
              className="profile-photo-btn"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploadingPhoto}
            >
              <ProfileIcon name="camera" /> {uploadingPhoto ? "Subiendo..." : "Cambiar foto"}
            </button>
            <span className="profile-role-chip profile-role-chip--client">
              <ProfileIcon name="badge" /> Cliente
            </span>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            style={{ display: "none" }}
            onChange={handlePhotoChange}
          />
          {photoError ? <small className="profile-photo-msg profile-photo-msg--error">{photoError}</small> : null}
          {photoSuccess ? <small className="profile-photo-msg profile-photo-msg--success">Foto actualizada ✓</small> : null}
        </div>
      </header>

      <article className="profile-info-card">
        <h2><ProfileIcon name="user" /> Datos de la cuenta</h2>
        <div className="profile-info-grid">
          <div className="profile-info-row">
            <span className="profile-info-label"><ProfileIcon name="user" /> Nombre completo</span>
            <strong>{fullName}</strong>
          </div>
          <div className="profile-info-row">
            <span className="profile-info-label"><ProfileIcon name="mail" /> Correo electrónico</span>
            <strong>{user?.email || "—"}</strong>
          </div>
          {client?.phone ? (
            <div className="profile-info-row">
              <span className="profile-info-label"><ProfileIcon name="phone" /> Teléfono</span>
              <strong>{client.phone}</strong>
            </div>
          ) : null}
          <div className="profile-info-row">
            <span className="profile-info-label"><ProfileIcon name="shield" /> Rol</span>
            <strong>Cliente</strong>
          </div>
          <div className="profile-info-row">
            <span className="profile-info-label"><ProfileIcon name="id" /> ID de usuario</span>
            <strong className="profile-info-mono">{user?.id || "—"}</strong>
          </div>
        </div>
      </article>

      {hasPhysicalData ? (
        <article className="profile-info-card">
          <h2><ProfileIcon name="ruler" /> Mis datos físicos</h2>
          <div className="profile-info-grid">
            {client?.age ? (
              <div className="profile-info-row">
                <span className="profile-info-label"><ProfileIcon name="calendar" /> Edad</span>
                <strong>{client.age} años</strong>
              </div>
            ) : null}
            {client?.sex ? (
              <div className="profile-info-row">
                <span className="profile-info-label"><ProfileIcon name="user" /> Sexo</span>
                <strong>{client.sex === "FEMALE" ? "Mujer" : "Hombre"}</strong>
              </div>
            ) : null}
            {client?.heightCm ? (
              <div className="profile-info-row">
                <span className="profile-info-label"><ProfileIcon name="height" /> Altura</span>
                <strong>{client.heightCm} cm</strong>
              </div>
            ) : null}
            {client?.currentWeightKg ? (
              <div className="profile-info-row">
                <span className="profile-info-label"><ProfileIcon name="weight" /> Peso actual</span>
                <strong>{client.currentWeightKg} kg</strong>
              </div>
            ) : null}
            {client?.targetWeightKg ? (
              <div className="profile-info-row">
                <span className="profile-info-label"><ProfileIcon name="trophy" /> Peso meta</span>
                <strong>{client.targetWeightKg} kg</strong>
              </div>
            ) : null}
          </div>
        </article>
      ) : null}

      {hasGoalData ? (
        <article className="profile-info-card">
          <h2><ProfileIcon name="target" /> Objetivo y plan</h2>
          <div className="profile-info-grid">
            {client?.objective ? (
              <div className="profile-info-row">
                <span className="profile-info-label"><ProfileIcon name="target" /> Objetivo principal</span>
                <strong>{client.objective}</strong>
              </div>
            ) : null}
            {client?.planName || client?.notes ? (
              <div className="profile-info-row">
                <span className="profile-info-label"><ProfileIcon name="plan" /> Plan asignado</span>
                <strong>{client.planName || client.notes}</strong>
              </div>
            ) : null}
            {client?.planStartDate ? (
              <div className="profile-info-row">
                <span className="profile-info-label"><ProfileIcon name="calendar" /> Inicio del plan</span>
                <strong>{formatPlanDate(client.planStartDate)}</strong>
              </div>
            ) : null}
            {client?.planEndDate ? (
              <div className="profile-info-row">
                <span className="profile-info-label"><ProfileIcon name="calendar" /> Fin del plan</span>
                <strong>{formatPlanDate(client.planEndDate)}</strong>
              </div>
            ) : null}
          </div>
          <small className="profile-info-note">
            Estos datos los actualiza tu coach. Si algo cambió, escríbele por Mensajes.
          </small>
        </article>
      ) : null}
    </section>
  );
}
